import { useCallback, useEffect, useState } from 'react'
import {
  api,
  ApiError,
  type DeadMessage,
  type IndexStatus,
  type StuckRecord,
} from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, ConfirmDialog, Notice, Spinner } from '../ui/controls'

/**
 * Управление поисковым индексом (п. 4.18).
 *
 * Экран отвечает на один вопрос: доходят ли изменения каталога до витрины,
 * и если нет — где остановились. Путь изменения состоит из трёх участков,
 * и показаны все три: очередь публикации в базе, очереди брокера, сам
 * индекс. Расхождение между числом публикуемых товаров и документов
 * в индексе вынесено отдельно (А-1702) — это главный признак того,
 * что витрина отстала.
 */
export default function IndexPage() {
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.index

  const [status, setStatus] = useState<IndexStatus | null>(null)
  const [stuck, setStuck] = useState<StuckRecord[]>([])
  const [dead, setDead] = useState<{ queue: string; messages: DeadMessage[] } | null>(null)
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)
  const [pending, setPending] = useState(false)
  const [confirmPurge, setConfirmPurge] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [s, k] = await Promise.all([api.indexStatus(), api.stuckRecords()])
      setStatus(s)
      setStuck(k)
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Состояние недоступно' })
    }
  }, [])

  useEffect(() => {
    void load()
    // Очередь публикации живёт секундами: замерший показатель на экране
    // выглядел бы поломкой там, где всё работает.
    const timer = setInterval(() => void load(), 5000)
    return () => clearInterval(timer)
  }, [load])

  async function act(what: string, fn: () => Promise<unknown>) {
    setPending(true)
    setNotice(null)
    try {
      await fn()
      await load()
      setNotice({ kind: 'success', text: what })
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось' })
    } finally {
      setPending(false)
    }
  }

  if (!status) return <Spinner label="Загрузка состояния…" />

  const deadQueues = status.queues.filter((q) => q.dead)
  const deadTotal = deadQueues.reduce((n, q) => n + q.messages, 0)

  return (
    <section className="max-w-5xl">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Поисковый индекс</h2>
          <p className="mt-0.5 text-sm text-ink-500">
            Псевдоним <span className="font-mono">{status.alias}</span>. Изменения каталога
            доходят до витрины через очередь публикации и брокер.
          </p>
        </div>
        {canWrite && (
          <Button
            onClick={() =>
              act('Переиндексация поставлена в очередь', async () => {
                const r = await api.reindexAll()
                setNotice({ kind: 'success', text: `Поставлено в очередь: ${r.queued}` })
              })
            }
            disabled={pending}
          >
            Переиндексировать всё
          </Button>
        )}
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {/* Главный признак: витрина отстала (А-1702) */}
      {status.mismatch && (
        <div className="mb-4 rounded-lg border border-warning-500/40 bg-warning-100 px-4 py-3 dark:bg-warning-700/25">
          <p className="text-sm text-warning-700 dark:text-warning-100">
            В индексе {status.indexed.toLocaleString('ru')} документов, а публикации подлежат{' '}
            {status.publishable.toLocaleString('ru')} товаров. Расхождение{' '}
            {Math.abs(status.publishable - status.indexed).toLocaleString('ru')}.
          </p>
          <p className="mt-1 text-xs text-warning-700/80 dark:text-warning-100/80">
            Если очередь публикации пуста, а расхождение осталось — нужна переиндексация.
          </p>
        </div>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label="В индексе" value={status.indexed} />
        <Figure label="Подлежат публикации" value={status.publishable} />
        <Figure
          label="Ждут передачи"
          value={status.pending}
          hint={
            status.oldest_age_seconds !== null
              ? `старейшей ${formatAge(status.oldest_age_seconds)}`
              : undefined
          }
          warn={status.oldest_age_seconds !== null && status.oldest_age_seconds > 60}
        />
        <Figure
          label="Последняя передача"
          text={status.last_published_at ? formatMoment(status.last_published_at) : 'не было'}
        />
      </div>

      {/* Очереди брокера (А-1701) */}
      <Block title="Очереди брокера">
        {status.broker_error ? (
          <p className="text-sm text-danger-700 dark:text-danger-100">
            Связи с брокером нет: {status.broker_error}
          </p>
        ) : status.queues.length === 0 ? (
          <p className="text-sm text-ink-500">Очереди ещё не объявлены.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-ink-500">
              <tr className="border-b border-ink-200 dark:border-ink-800">
                <th className="py-2 pr-4 text-left font-medium">Очередь</th>
                <th className="py-2 pr-6 text-right font-medium">Сообщений</th>
                <th className="py-2 pr-6 text-right font-medium">Потребителей</th>
                <th className="py-2 text-left font-medium" />
              </tr>
            </thead>
            <tbody>
              {status.queues.map((q) => (
                <tr key={q.name} className="border-b border-ink-100 dark:border-ink-900">
                  <td className="py-1.5 pr-4 font-mono text-xs">
                    {q.name}
                    {q.dead && (
                      <span className="ml-2 rounded-full bg-danger-100 px-2 py-0.5 text-2xs text-danger-700 dark:bg-danger-700/25 dark:text-danger-100">
                        недоставленные
                      </span>
                    )}
                  </td>
                  <td
                    className={`py-1.5 pr-6 text-right tabular-nums ${
                      q.dead && q.messages > 0
                        ? 'font-semibold text-danger-700 dark:text-danger-100'
                        : ''
                    }`}
                  >
                    {q.messages}
                  </td>
                  <td className="py-1.5 pr-6 text-right tabular-nums text-ink-500">
                    {q.consumers}
                  </td>
                  <td className="py-1.5">
                    {q.dead && q.messages > 0 && (
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            void api
                              .deadLetters(q.name)
                              .then((m) => setDead({ queue: q.name, messages: m }))
                              .catch((e) =>
                                setNotice({
                                  kind: 'error',
                                  text: e instanceof ApiError ? e.message : 'Не удалось прочитать',
                                }),
                              )
                          }
                          className="text-xs text-brand-600 hover:underline dark:text-brand-300"
                        >
                          Посмотреть
                        </button>
                        {canWrite && (
                          <>
                            <button
                              type="button"
                              disabled={pending}
                              onClick={() =>
                                act('Сообщения возвращены в работу', async () => {
                                  const r = await api.requeueDead(q.name)
                                  setDead(null)
                                  setNotice(
                                    r.skipped > 0
                                      ? {
                                          kind: 'warning',
                                          text:
                                            `Возвращено в работу ${r.requeued}, осталось ${r.skipped}` +
                                            (r.reason ? `: ${r.reason}` : ''),
                                        }
                                      : { kind: 'success', text: `Возвращено в работу: ${r.requeued}` },
                                  )
                                })
                              }
                              className="text-xs text-brand-600 hover:underline dark:text-brand-300"
                            >
                              Вернуть в работу
                            </button>
                            <button
                              type="button"
                              disabled={pending}
                              onClick={() => setConfirmPurge(q.name)}
                              className="text-xs text-danger-700 hover:underline dark:text-danger-100"
                            >
                              Удалить
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {deadTotal === 0 && status.queues.length > 0 && (
          <p className="mt-3 text-xs text-ink-500">
            Недоставленных сообщений нет — все изменения дошли до индекса.
          </p>
        )}
      </Block>

      {/* Тела недоставленных сообщений (А-1706) */}
      {dead && (
        <Block title={`Недоставленные: ${dead.queue}`}>
          {dead.messages.length === 0 ? (
            <p className="text-sm text-ink-500">Очередь пуста.</p>
          ) : (
            <ul className="space-y-3">
              {dead.messages.map((m, i) => (
                <li
                  key={i}
                  className="rounded-md border border-ink-200 p-3 dark:border-ink-800"
                >
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
                    <span className="font-mono text-ink-700 dark:text-ink-300">
                      {m.routing_key}
                    </span>
                    <span className="text-danger-700 dark:text-danger-100">{m.reason}</span>
                    <span className="text-ink-500">попыток {m.attempts}</span>
                  </div>
                  <pre className="mt-2 overflow-x-auto rounded bg-ink-50 p-2 text-2xs leading-relaxed text-ink-700 dark:bg-ink-900 dark:text-ink-300">
                    {m.body}
                  </pre>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={() => setDead(null)}
            className="mt-3 text-xs text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
          >
            Свернуть
          </button>
        </Block>
      )}

      {/* Записи, не переданные в брокер (А-1705) */}
      <Block title="Не переданы в брокер">
        {stuck.length === 0 ? (
          <p className="text-sm text-ink-500">
            Записей с неудачными попытками нет. Изменения передаются брокеру без задержек.
          </p>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-ink-600 dark:text-ink-400">
                Записей с неудачными попытками: {stuck.length}
              </p>
              {canWrite && (
                <Button
                  onClick={() =>
                    act('Записи поставлены на повторную передачу', async () => {
                      const r = await api.retryStuck()
                      setNotice({ kind: 'success', text: `К повтору: ${r.retried}` })
                    })
                  }
                  disabled={pending}
                >
                  Повторить все
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[44rem] text-sm">
                <thead className="text-xs uppercase text-ink-500">
                  <tr className="border-b border-ink-200 dark:border-ink-800">
                    <th className="py-2 pr-4 text-left font-medium">Запись</th>
                    <th className="py-2 pr-4 text-left font-medium">Ключ</th>
                    <th className="py-2 pr-6 text-right font-medium">Попыток</th>
                    <th className="py-2 text-left font-medium">Ошибка</th>
                  </tr>
                </thead>
                <tbody>
                  {stuck.map((s) => (
                    <tr key={s.id} className="border-b border-ink-100 dark:border-ink-900">
                      <td className="py-1.5 pr-4">
                        {s.title ?? `${s.entity_type} №${s.entity_id}`}
                      </td>
                      <td className="py-1.5 pr-4 font-mono text-xs">{s.routing_key}</td>
                      <td className="py-1.5 pr-6 text-right tabular-nums">{s.attempts}</td>
                      <td className="max-w-md truncate py-1.5 text-xs text-danger-700 dark:text-danger-100">
                        {s.last_error ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Block>

      {confirmPurge !== null && (
      <ConfirmDialog
        title="Удалить недоставленные сообщения?"
        message={
          `Сообщения очереди ${confirmPurge} будут удалены безвозвратно. ` +
          'Изменения, которые они несли, до индекса не дойдут — товары придётся переиндексировать.'
        }
        confirmLabel="Удалить"
        pending={pending}
        onCancel={() => setConfirmPurge(null)}
        onConfirm={() => {
          const queue = confirmPurge!
          setConfirmPurge(null)
          void act('Сообщения удалены', async () => {
            const r = await api.purgeDead(queue)
            setDead(null)
            setNotice({ kind: 'warning', text: `Удалено сообщений: ${r.purged}` })
          })
        }}
      />
      )}
    </section>
  )
}

// --------------------------------------------------------------------------

function Figure({
  label,
  value,
  text,
  hint,
  warn,
}: {
  label: string
  value?: number
  text?: string
  hint?: string
  warn?: boolean
}) {
  return (
    <div className="rounded-lg border border-ink-200 bg-white p-3 dark:border-ink-800 dark:bg-ink-950">
      <div className="text-xs uppercase text-ink-500">{label}</div>
      <div
        className={`mt-1 text-xl font-semibold tabular-nums ${
          warn ? 'text-warning-700 dark:text-warning-100' : 'text-ink-900 dark:text-ink-50'
        }`}
      >
        {text ?? (value ?? 0).toLocaleString('ru')}
      </div>
      {hint && <div className="mt-0.5 text-xs text-ink-500">{hint}</div>}
    </div>
  )
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6 rounded-lg border border-ink-200 bg-white p-4 dark:border-ink-800 dark:bg-ink-950">
      <h3 className="mb-3 text-sm font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
      {children}
    </div>
  )
}

/** Возраст записи словами: «12 с», «3 мин», «2 ч». */
function formatAge(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} с`
  if (seconds < 3600) return `${Math.round(seconds / 60)} мин`
  return `${Math.round(seconds / 3600)} ч`
}

function formatMoment(iso: string): string {
  return iso.slice(0, 19).replace('T', ' ')
}
